import React, { useState, useEffect } from 'react';
import { Layout, Drawer } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';
// Import des icônes modernes pour améliorer l'interface
import {
  HomeIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import logo from '../../assets/logo.png';
// Thème citron / noir / blanc du haut et du bas des pages publiques (préfixe "pl-")
import './PublicLayout.css';

const { Content } = Layout;

// Services affichés dans le pied de page (données séparées du rendu)
const FOOTER_SERVICES = [
  'Messagerie WhatsApp',
  'Gestion des commandes',
  'Point de vente (POS)',
  'Vitrine en ligne',
  'Assistant IA'
];

// Chiffres du pied de page (issus de la configuration réelle des offres)
const FOOTER_STATS = [
  { valeur: '14 jours', libelle: 'Essai gratuit' },
  { valeur: '5', libelle: 'Offres' }
];

// Coordonnées affichées dans le pied de page
const FOOTER_CONTACTS = [
  { icon: ChatBubbleLeftRightIcon, label: 'Email', valeur: 'shopmercuri@gmail.com' },
  { icon: InformationCircleIcon, label: 'Téléphone', valeur: '+225 07 59 95 47 08' },
  { icon: HomeIcon, label: 'Adresse', valeur: "Abidjan, Côte d'Ivoire" }
];

// Liens légaux du pied de page
const LEGAL_LINKS = [
  { to: '/terms', label: "Conditions d'utilisation" },
  { to: '/privacy', label: 'Confidentialité' },
  { to: '/cookies', label: 'Cookies' }
];

const PublicLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false); // État pour ajouter l'ombre du header au scroll
  const location = useLocation();

  // Navigation avec icônes modernes
  const navItems = [
    { key: '/', label: 'Accueil', path: '/', icon: HomeIcon },
    { key: '/features', label: 'Fonctionnalités', path: '/features', icon: SparklesIcon },
    { key: '/pricing', label: 'Tarifs', path: '/pricing', icon: CurrencyDollarIcon },
    { key: '/about', label: 'À propos', path: '/about', icon: InformationCircleIcon },
    { key: '/contact', label: 'Contact', path: '/contact', icon: ChatBubbleLeftRightIcon },
  ];

  // Gérer l'effet de scroll pour le header
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Détecter quelle page est active
  const getSelectedKeys = () => {
    const pathname = location.pathname;
    const item = navItems.find(item => item.path === pathname);
    return item ? [item.key] : ['/'];
  };

  return (
    <Layout className="pl-layout">
      {/* Header : fond blanc, onglet actif en citron */}
      <header className={`pl-header ${scrolled ? 'pl-header-scrolled' : ''}`}>
        <div className="pl-header-inner">
          {/* Logo et branding */}
          <Link to="/" className="pl-brand">
            <span className="pl-brand-icon"><img src={logo} alt="Mercuri Shop Logo" /></span>
            <span className="pl-brand-text">
              <strong>Mercuri Shop</strong>
              <small>WhatsApp Business Solution</small>
            </span>
          </Link>

          {/* Menu desktop */}
          <nav className="pl-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = getSelectedKeys().includes(item.key);
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`pl-nav-link ${isActive ? 'pl-nav-link-active' : ''}`}
                >
                  <Icon className="pl-icon" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Actions desktop */}
          <div className="pl-actions">
            <Link to="/login" className="pl-btn pl-btn-ghost">Se connecter</Link>
            <Link to="/register" className="pl-btn pl-btn-accent">
              S'inscrire gratuitement
              <ChevronRightIcon className="pl-icon" />
            </Link>
          </div>

          {/* Toggle menu mobile */}
          <button
            type="button"
            className="pl-burger"
            onClick={toggleMobileMenu}
            aria-label="Ouvrir le menu"
          >
            <MenuOutlined />
          </button>
        </div>
      </header>

      {/* Drawer menu mobile */}
      <Drawer
        placement="right"
        closable={false}
        onClose={closeMobileMenu}
        open={mobileMenuOpen}
        width="85%"
        bodyStyle={{ padding: 0 }}
        rootClassName="pl-drawer"
      >
        {/* En-tête du menu mobile */}
        <div className="pl-drawer-head">
          <Link to="/" onClick={closeMobileMenu} className="pl-brand">
            <span className="pl-brand-icon"><img src={logo} alt="Mercuri Shop Logo" /></span>
            <span className="pl-brand-text"><strong>Mercuri Shop</strong></span>
          </Link>
          <button type="button" className="pl-burger" onClick={closeMobileMenu} aria-label="Fermer le menu">
            <CloseOutlined />
          </button>
        </div>

        {/* Navigation mobile */}
        <div className="pl-drawer-body">
          <nav className="pl-drawer-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = getSelectedKeys().includes(item.key);
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`pl-drawer-link ${isActive ? 'pl-nav-link-active' : ''}`}
                >
                  <Icon className="pl-icon" />
                  <span>{item.label}</span>
                  <ChevronRightIcon className="pl-icon pl-drawer-chevron" />
                </Link>
              );
            })}
          </nav>

          {/* Actions mobiles */}
          <div className="pl-drawer-actions">
            <Link to="/login" onClick={closeMobileMenu} className="pl-btn pl-btn-outline">Se connecter</Link>
            <Link to="/register" onClick={closeMobileMenu} className="pl-btn pl-btn-accent">S'inscrire gratuitement</Link>
          </div>
        </div>
      </Drawer>

      {/* Main content avec padding adapté à la hauteur du header */}
      <Content className="pl-content">
        {/* Afficher children si fournis, sinon utiliser Outlet pour les routes enfants */}
        {children || <Outlet />}
      </Content>

      {/* Footer : fond noir, accents citron */}
      <footer className="pl-footer">
        <div className="pl-footer-inner">
          <div className="pl-footer-grid">
            {/* Branding */}
            <div>
              <Link to="/" className="pl-brand pl-brand-light">
                <span className="pl-brand-icon"><img src={logo} alt="Mercuri Shop Logo" /></span>
                <span className="pl-brand-text"><strong>Mercuri Shop</strong></span>
              </Link>
              <p className="pl-footer-about">
                La plateforme pour gérer vos commandes, vos livraisons et votre boutique WhatsApp en Côte d'Ivoire.
              </p>
              <div className="pl-footer-stats">
                {FOOTER_STATS.map((stat) => (
                  <div key={stat.libelle}><strong>{stat.valeur}</strong><span>{stat.libelle}</span></div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h3 className="pl-footer-title">Navigation</h3>
              <ul className="pl-footer-list">
                {navItems.map(item => (
                  <li key={item.key}><Link to={item.path}>{item.label}</Link></li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="pl-footer-title">Services</h3>
              <ul className="pl-footer-list">
                {FOOTER_SERVICES.map(service => (
                  <li key={service} className="pl-footer-service">{service}</li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="pl-footer-title">Contact</h3>
              <ul className="pl-footer-list">
                {FOOTER_CONTACTS.map(({ icon: Icon, label, valeur }) => (
                  <li key={label} className="pl-footer-contact">
                    <Icon className="pl-icon" />
                    <span><strong>{label}</strong>{valeur}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Copyright et liens légaux */}
          <div className="pl-footer-bottom">
            <span>&copy; {new Date().getFullYear()} Mercuri Shop. Tous droits réservés.</span>
            <div className="pl-footer-legal">
              {LEGAL_LINKS.map(lien => (
                <Link key={lien.to} to={lien.to}>{lien.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </Layout>
  );
};

export default PublicLayout;
